import { Navigate, useLocation } from 'react-router-dom';
import { Spinner } from '@/components/atoms/Spinner';
import { useAuth } from '@/hooks/useAuth';
import { usePrefersAdminExperience } from '@/hooks/usePrefersAdminExperience';
import { canAccessArea, getHomeRoute } from '@/utils/roleRouting';
import { ROUTES } from '@/constants/routes';
import type { ReactNode } from 'react';
import type { UserRole } from '@/types/auth.types';
import styles from './ProtectedRoute.module.scss';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: UserRole[];
}

export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const prefersAdmin = usePrefersAdminExperience();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner size="lg" aria-label="Loading" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to={ROUTES.AUTH.LOGIN} state={{ from: location }} replace />;
  }

  // Comprobación de acceso antes de renderizar children (evita flashes). Un
  // alumno con isCompanyAdmin también puede entrar al área de empresa; si no
  // tiene acceso, lo mandamos a su "home" (device-aware para el dual).
  if (allowedRoles && !canAccessArea(user, allowedRoles)) {
    return <Navigate to={getHomeRoute(user, prefersAdmin)} replace />;
  }

  return <>{children}</>;
}

export default ProtectedRoute;
